import * as React from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import { alpha, useTheme } from '@mui/material/styles'
import { format } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { moreEventsLabel } from '../../dateFormat'
import { visibleRows } from '../../allDayBars'
import { EventTile } from './EventTile'
import { COARSE_POINTER, TOUCH_TARGET_PX } from '../touch'
import { useOpenCreateEvent } from '../createEventContext'

type Props = {
  day: Date
  events: CalendarEvent[]
  /** False for days spilling in from the adjacent month — rendered muted. */
  inMonth: boolean
  isToday: boolean
  /** Opens the day view; the "+N more" link needs it to be actionable. */
  onShowDay?: (day: Date) => void
  /** Rows reserved at the top for multi-day bars the grid draws over the cell. */
  barSlots?: number
  /** Multi-day bars that did not fit; they count towards "+N more". */
  hiddenBars?: number
  /** Rows the cell shows before "+N more"; defaults to what `events` alone need. */
  rowLimit?: number
  /** Places the cell on the month grid's explicit rows and columns. */
  gridArea?: string
}

/** Height of a chip row; the grid sizes its multi-day bars to match. */
export const MONTH_ROW_PX = 20

/** One day cell of the month grid: a date badge + up to three rows of chips. */
export function MonthCell({
  day,
  events,
  inMonth,
  isToday,
  onShowDay,
  barSlots = 0,
  hiddenBars = 0,
  rowLimit = visibleRows(0, events.length),
  gridArea,
}: Props) {
  const theme = useTheme()
  const openCreate = useOpenCreateEvent()
  const shown = events.slice(0, Math.max(0, rowLimit - barSlots))
  const hiddenCount = events.length - shown.length + hiddenBars

  return (
    <Box
      data-test-id="month-cell"
      sx={{
        gridArea,
        position: 'relative',
        minHeight: { xs: '62px', sm: '78px', md: '96px' },
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '4px',
        borderRadius: '8px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: inMonth
          ? theme.palette.background.paper
          : theme.palette.grey[50],
      }}
    >
      {openCreate && (
        // SH-2133: an underlay rather than a wrapper, because the chips inside
        // are themselves ButtonBases. A month cell carries no time axis, so the
        // prefill is date-only and the pointer position is never read.
        <ButtonBase
          data-test-id="month-cell-create"
          onClick={() => openCreate({ day, start: null })}
          aria-label={`Add an event on ${format(day, 'MMMM d')}`}
          // A full-cell ripple on every create click; the design specifies
          // only a hover tint on this surface.
          disableRipple
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            display: 'block',
            zIndex: 0,
            cursor: 'pointer',
            '&:hover': { backgroundColor: theme.palette.grey[100] },
            '&.Mui-focusVisible': {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: -2,
            },
          }}
        />
      )}
      <Box
        data-test-id="month-date-badge"
        data-today={isToday ? 'true' : 'false'}
        // Stacked above the create underlay but non-interactive, so clicks on the date number still reach it.
        sx={{
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '22px',
          height: '22px',
          mb: '2px',
          borderRadius: '50%',
          fontFamily: theme.typography.fontFamily,
          fontSize: '12px',
          fontWeight: isToday ? 700 : 500,
          color: isToday
            ? theme.palette.primary.main
            : inMonth
              ? theme.palette.text.primary
              : theme.palette.text.secondary,
          backgroundColor: isToday
            ? alpha(theme.palette.primary.main, 0.12)
            : undefined,
        }}
      >
        {format(day, 'd')}
      </Box>

      <Box
        // Full-width wrapper: gaps and space beside a short chip fall through to the create underlay; chips get their clicks back.
        sx={{
          position: 'relative',
          zIndex: 1,
          pointerEvents: 'none',
          '& > *': { pointerEvents: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        {Array.from({ length: barSlots }, (_, i) => (
          <Box
            key={`bar-slot-${i}`}
            aria-hidden
            sx={{
              height: `${MONTH_ROW_PX}px`,
              '&&': { pointerEvents: 'none' },
            }}
          />
        ))}
        {shown.map((event) => (
          <EventTile key={event.id} event={event} variant="monthChip" />
        ))}
        {hiddenCount > 0 && (
          <ButtonBase
            data-test-id="month-more"
            disabled={!onShowDay}
            onClick={() => onShowDay?.(day)}
            aria-label={`${moreEventsLabel(hiddenCount)} on ${format(day, 'MMMM d')}, open day view`}
            // The cell has only a min-height, so on touch the row grows to fit the touch target.
            sx={{
              justifyContent: 'flex-start',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: theme.typography.fontFamily,
              fontSize: '12px',
              fontWeight: 600,
              color: theme.palette.primary.main,
              [COARSE_POINTER]: { minHeight: TOUCH_TARGET_PX },
              '&:hover': { backgroundColor: theme.palette.grey[100] },
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
            }}
          >
            +{hiddenCount} more
          </ButtonBase>
        )}
      </Box>
    </Box>
  )
}
